import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

function useCountUp(target: number, duration: number = 2000, active: boolean = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return;
    let startTime: number | null = null;
    const step = (ts: number) => {
      if (!startTime) startTime = ts;
      const p = Math.min((ts - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setCount(Math.floor(eased * target));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, active]);
  return count;
}

const LandingPage: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [statsActive, setStatsActive] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setStatsActive(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) obs.observe(statsRef.current);
    return () => { clearTimeout(t); obs.disconnect(); };
  }, []);

  const companies  = useCountUp(4200, 2000, statsActive);
  const invoices   = useCountUp(1300000, 2200, statsActive);
  const accuracy   = useCountUp(98, 1800, statsActive);

  const features = [
    { icon: '◆', title: 'Invoicing & Billing',      desc: 'Generate professional invoices, automate reminders, and track payment status in real time.' },
    { icon: '◈', title: 'Financial Reports',         desc: 'P&L, balance sheet, and cash flow statements rendered live — export to PDF in seconds.' },
    { icon: '◇', title: 'Bank Reconciliation',       desc: 'Match transactions automatically. Surface discrepancies before they compound.' },
    { icon: '○', title: 'Purchase Orders',            desc: 'End-to-end procurement: POs tied to goods receipts and vendor bills.' },
    { icon: '□', title: 'Inventory Control',          desc: 'Multi-warehouse stock tracking, transfers, and service warehouse management.' },
    { icon: '△', title: 'Multi-Role Access',          desc: 'Eight granular permission tiers — from Super Admin to Technician.' },
    { icon: '⬡', title: 'Rentals & Equipment',       desc: 'Machine rental tracking, refill cycles, and customer equipment reports.' },
    { icon: '◉', title: 'Customer & Vendor CRM',     desc: 'Unified records for customers, vendors, jobs, and all related transactions.' },
    { icon: '◌', title: 'Estimates & Quotes',         desc: 'Turn estimates into invoices in one click. Full approval workflow.' },
  ];

  const testimonials = [
    { quote: 'The reconciliation module alone saves us 12 hours every month. It\'s become indispensable.', author: 'Sarah K.', role: 'CFO, Meridian Group' },
    { quote: 'We replaced three tools with this one platform. The ROI was immediate and the team adopted it within days.', author: 'Ahmad R.', role: 'Finance Director, NovaTech' },
    { quote: 'The permission system is enterprise-grade. Exactly what we needed to scale without sacrificing control.', author: 'Elena V.', role: 'Controller, Apex Industries' },
  ];

  const cls = (base: string, condition: boolean) => `${base} ${condition ? 'lp-visible' : ''}`;

  return (
    <div className="lp-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Cinzel:wght@400;500;600&family=Jost:wght@300;400;500;600&display=swap');

        .lp-root {
          background: #07070f;
          min-height: 100vh;
          font-family: 'Inter', sans-serif;
          color: #e8e0d0;
          overflow-x: hidden;
        }

        /* ── NAV ─────────────────────────────────────────── */
        .lp-nav {
          position: fixed; top: 0; left: 0; right: 0; z-index: 100;
          display: flex; align-items: center; justify-content: space-between;
          padding: 22px 72px;
          background: linear-gradient(to bottom, rgba(7,7,15,0.97), rgba(7,7,15,0.7) 80%, transparent);
          backdrop-filter: blur(8px);
        }
        .lp-logo {
          font-family: 'Inter', sans-serif; font-size: 17px; font-weight: 600;
          letter-spacing: 0.35em; color: #c9a55a; text-transform: uppercase;
          text-decoration: none;
        }
        .lp-nav-links { display: flex; gap: 40px; list-style: none; margin: 0; padding: 0; }
        .lp-nav-links a {
          font-size: 11px; font-weight: 400; letter-spacing: 0.2em; text-transform: uppercase;
          color: #7a6a55; text-decoration: none; transition: color 0.25s;
        }
        .lp-nav-links a:hover { color: #c9a55a; }
        .lp-nav-cta {
          font-size: 11px; font-weight: 500; letter-spacing: 0.2em; text-transform: uppercase;
          padding: 11px 28px; border: 1px solid #c9a55a; color: #c9a55a;
          background: transparent; text-decoration: none; transition: all 0.25s;
        }
        .lp-nav-cta:hover { background: #c9a55a; color: #07070f; }

        /* ── HERO ────────────────────────────────────────── */
        .lp-hero {
          min-height: 100vh;
          display: flex; align-items: center;
          padding: 160px 72px 100px;
          position: relative; overflow: hidden;
        }
        .lp-grid {
          position: absolute; inset: 0; pointer-events: none;
          background-image:
            linear-gradient(rgba(241,245,249,1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(241,245,249,1) 1px, transparent 1px);
          background-size: 80px 80px;
        }
        .lp-orb-1 {
          position: absolute; top: -200px; right: -80px;
          width: 700px; height: 700px; border-radius: 50%;
          background: radial-gradient(circle, rgba(201,165,90,0.10) 0%, transparent 70%);
          filter: blur(100px); pointer-events: none;
        }
        .lp-orb-2 {
          position: absolute; bottom: -100px; left: 5%;
          width: 500px; height: 500px; border-radius: 50%;
          background: radial-gradient(circle, rgba(80,60,160,0.07) 0%, transparent 70%);
          filter: blur(120px); pointer-events: none;
        }
        .lp-hero-inner { position: relative; z-index: 2; max-width: 820px; }

        /* Fade-up transitions */
        .lp-fade { opacity: 0; transform: translateY(24px); transition: opacity 0.9s ease, transform 0.9s ease; }
        .lp-fade.lp-visible { opacity: 1; transform: translateY(0); }
        .lp-fade-d1 { transition-delay: 0.1s; }
        .lp-fade-d2 { transition-delay: 0.25s; }
        .lp-fade-d3 { transition-delay: 0.4s; }
        .lp-fade-d4 { transition-delay: 0.55s; }

        .lp-eyebrow {
          display: flex; align-items: center; gap: 16px;
          font-family: 'Inter', sans-serif; font-size: 10px; font-weight: 400;
          letter-spacing: 0.5em; text-transform: uppercase; color: #c9a55a;
          margin-bottom: 36px;
        }
        .lp-eyebrow::before { content: ''; width: 48px; height: 1px; background: #c9a55a; }

        .lp-headline {
          font-family: 'Inter', sans-serif;
          font-size: clamp(68px, 9vw, 116px);
          font-weight: 300; line-height: 0.88; color: #f0e8d8;
          margin: 0 0 44px;
        }
        .lp-headline em {
          font-style: italic;
          background: linear-gradient(135deg, #c9a55a, #e8c97a, #a87a3a);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
        }

        .lp-sub {
          font-size: 17px; font-weight: 300; line-height: 1.85;
          color: #6a5a45; max-width: 560px; margin: 0 0 56px;
        }

        .lp-ctas { display: flex; gap: 20px; align-items: center; }
        .lp-btn-primary {
          font-size: 11px; font-weight: 500; letter-spacing: 0.2em; text-transform: uppercase;
          padding: 18px 52px;
          background: linear-gradient(135deg, #c9a55a, #b8943a);
          color: #07070f; text-decoration: none; display: inline-block;
          transition: all 0.25s;
        }
        .lp-btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 20px 60px rgba(217,119,6,0.3);
        }
        .lp-btn-ghost {
          font-size: 11px; font-weight: 400; letter-spacing: 0.2em; text-transform: uppercase;
          color: #7a6a55; text-decoration: none; display: inline-flex; align-items: center; gap: 10px;
          transition: color 0.25s;
        }
        .lp-btn-ghost:hover { color: #c9a55a; }
        .lp-btn-ghost::after { content: '→'; transition: transform 0.25s; }
        .lp-btn-ghost:hover::after { transform: translateX(4px); }

        /* ── STATS ───────────────────────────────────────── */
        .lp-stats {
          padding: 100px 72px;
          border-top: 1px solid rgba(148,163,184,0.2);
          border-bottom: 1px solid rgba(148,163,184,0.2);
        }
        .lp-stats-grid {
          display: grid; grid-template-columns: repeat(3, 1fr);
          max-width: 900px; margin: 0 auto;
        }
        .lp-stat {
          padding: 0 48px; text-align: center;
          border-right: 1px solid rgba(148,163,184,0.15);
        }
        .lp-stat:last-child { border-right: none; }
        .lp-stat-n {
          font-family: 'Inter', sans-serif; font-size: 72px; font-weight: 300; line-height: 1;
          background: linear-gradient(135deg, #c9a55a, #e8c97a);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
        }
        .lp-stat-sub { font-size: 10px; font-weight: 400; letter-spacing: 0.3em; text-transform: uppercase; color: #4a3a25; margin-top: 8px; }

        /* ── FEATURES ────────────────────────────────────── */
        .lp-features { padding: 140px 72px; }
        .lp-section-tag {
          font-family: 'Inter', sans-serif; font-size: 10px; letter-spacing: 0.5em;
          text-transform: uppercase; color: #c9a55a; margin-bottom: 20px;
        }
        .lp-section-title {
          font-family: 'Inter', sans-serif; font-size: clamp(44px, 6vw, 64px);
          font-weight: 300; line-height: 1.1; color: #f0e8d8; margin: 0 0 72px;
        }
        .lp-section-title em { font-style: italic; color: #c9a55a; }
        .lp-feat-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1px; }
        .lp-feat-card {
          padding: 44px 36px; background: rgba(255,255,255,0.015);
          border: 1px solid rgba(226,232,240,0.7); position: relative;
          transition: all 0.35s; overflow: hidden;
        }
        .lp-feat-card::after {
          content: ''; position: absolute; inset: 0;
          background: linear-gradient(135deg, rgba(226,232,240,0.5), transparent);
          opacity: 0; transition: opacity 0.35s;
        }
        .lp-feat-card:hover { border-color: rgba(201,165,90,0.28); transform: translateY(-4px); box-shadow: 0 20px 60px rgba(0,0,0,0.5); }
        .lp-feat-card:hover::after { opacity: 1; }
        .lp-feat-icon { font-size: 18px; color: #c9a55a; margin-bottom: 24px; display: block; }
        .lp-feat-title {
          font-family: 'Inter', sans-serif; font-size: 12px; font-weight: 500;
          letter-spacing: 0.12em; text-transform: uppercase; color: #d8c8a8; margin-bottom: 14px;
        }
        .lp-feat-desc { font-size: 13px; font-weight: 300; line-height: 1.8; color: #4a3a25; }

        /* ── TESTIMONIALS ────────────────────────────────── */
        .lp-testimonials {
          padding: 120px 72px;
          background: rgba(201,165,90,0.025);
          border-top: 1px solid rgba(226,232,240,0.7);
        }
        .lp-testi-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 48px; margin-top: 72px; }
        .lp-testi { padding: 48px 36px; border-left: 2px solid rgba(148,163,184,0.35); }
        .lp-testi-q {
          font-family: 'Inter', sans-serif; font-size: 20px; font-weight: 400;
          line-height: 1.65; color: #b0a090; font-style: italic; margin-bottom: 28px;
        }
        .lp-testi-q::before { content: '\u201C'; color: #c9a55a; font-size: 28px; }
        .lp-testi-author {
          font-family: 'Inter', sans-serif; font-size: 10px; letter-spacing: 0.2em;
          text-transform: uppercase; color: #c9a55a;
        }
        .lp-testi-role { font-size: 11px; color: #4a3a25; margin-top: 4px; }

        /* ── CTA ─────────────────────────────────────────── */
        .lp-cta {
          padding: 160px 72px; text-align: center; position: relative; overflow: hidden;
        }
        .lp-cta::before {
          content: ''; position: absolute; inset: 0;
          background: radial-gradient(ellipse 70% 60% at 50% 50%, rgba(226,232,240,0.7), transparent);
        }
        .lp-cta-divider { width: 1px; height: 80px; background: linear-gradient(to bottom, transparent, #c9a55a, transparent); margin: 0 auto 48px; }
        .lp-cta-title {
          font-family: 'Inter', sans-serif;
          font-size: clamp(48px, 7vw, 88px);
          font-weight: 300; line-height: 1; color: #f0e8d8;
          margin: 0 0 20px; position: relative;
        }
        .lp-cta-title em { font-style: italic; color: #c9a55a; }
        .lp-cta-sub { font-size: 15px; font-weight: 300; color: #5a4a35; margin: 0 0 56px; position: relative; }
        .lp-cta-btn {
          font-size: 11px; font-weight: 500; letter-spacing: 0.3em; text-transform: uppercase;
          padding: 22px 80px; background: linear-gradient(135deg, #c9a55a, #b8943a);
          color: #07070f; text-decoration: none; display: inline-block; position: relative;
          transition: all 0.3s;
        }
        .lp-cta-btn:hover { transform: translateY(-3px); box-shadow: 0 28px 80px rgba(217,119,6,0.35); }

        /* ── FOOTER ──────────────────────────────────────── */
        .lp-footer {
          padding: 48px 72px;
          border-top: 1px solid rgba(148,163,184,0.15);
          display: flex; justify-content: space-between; align-items: center;
        }
        .lp-footer-logo { font-family: 'Inter', sans-serif; font-size: 14px; color: #c9a55a; letter-spacing: 0.3em; }
        .lp-footer-links { display: flex; gap: 32px; }
        .lp-footer-links a { font-size: 11px; letter-spacing: 0.15em; color: #3a2a15; text-decoration: none; transition: color 0.2s; }
        .lp-footer-links a:hover { color: #c9a55a; }
        .lp-footer-copy { font-size: 11px; color: #2a1a05; letter-spacing: 0.1em; }
      `}</style>

      {/* ── Navigation ───────────────────────────────────── */}
      <nav className="lp-nav">
        <Link to="/" className="lp-logo">Ledger Pro</Link>
        <ul className="lp-nav-links">
          <li><a href="#features">Features</a></li>
          <li><a href="#testimonials">Clients</a></li>
          <li><a href="#cta">Pricing</a></li>
        </ul>
        <Link to="/auth" className="lp-nav-cta">Sign In</Link>
      </nav>

      {/* ── Hero ─────────────────────────────────────────── */}
      <section className="lp-hero">
        <div className="lp-grid" />
        <div className="lp-orb-1" />
        <div className="lp-orb-2" />
        <div className="lp-hero-inner">
          <p className={cls('lp-eyebrow lp-fade', visible)}>Financial Intelligence Platform</p>
          <h1 className={cls('lp-headline lp-fade lp-fade-d1', visible)}>
            Where Finance<br />Meets <em>Mastery</em>
          </h1>
          <p className={cls('lp-sub lp-fade lp-fade-d2', visible)}>
            A complete accounting system built for organizations that demand precision.
            From invoice to reconciliation — every number, accounted for.
          </p>
          <div className={cls('lp-ctas lp-fade lp-fade-d3', visible)}>
            <Link to="/auth" className="lp-btn-primary">Begin Your Journey</Link>
            <a href="#features" className="lp-btn-ghost">Discover Features</a>
          </div>
        </div>
      </section>

      {/* ── Stats ────────────────────────────────────────── */}
      <section className="lp-stats" ref={statsRef}>
        <div className="lp-stats-grid">
          <div className="lp-stat">
            <div className="lp-stat-n">{accuracy}<span style={{ fontSize: 44 }}>%</span></div>
            <div className="lp-stat-sub">Accuracy Rate</div>
          </div>
          <div className="lp-stat">
            <div className="lp-stat-n">{companies.toLocaleString()}<span style={{ fontSize: 44 }}>+</span></div>
            <div className="lp-stat-sub">Active Companies</div>
          </div>
          <div className="lp-stat">
            <div className="lp-stat-n">{(invoices / 1000000).toFixed(1)}<span style={{ fontSize: 44 }}>M</span></div>
            <div className="lp-stat-sub">Invoices Processed</div>
          </div>
        </div>
      </section>

      {/* ── Features ─────────────────────────────────────── */}
      <section className="lp-features" id="features">
        <div className="lp-section-tag">Capabilities</div>
        <h2 className="lp-section-title">Built for the<br /><em>Entire Financial Lifecycle</em></h2>
        <div className="lp-feat-grid">
          {features.map((f, i) => (
            <div className="lp-feat-card" key={i}>
              <span className="lp-feat-icon">{f.icon}</span>
              <h3 className="lp-feat-title">{f.title}</h3>
              <p className="lp-feat-desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Testimonials ─────────────────────────────────── */}
      <section className="lp-testimonials" id="testimonials">
        <div className="lp-section-tag">Testimonials</div>
        <h2 className="lp-section-title" style={{ marginTop: 16 }}>Trusted by<br /><em>Finance Leaders</em></h2>
        <div className="lp-testi-grid">
          {testimonials.map((t, i) => (
            <div className="lp-testi" key={i}>
              <p className="lp-testi-q">{t.quote}</p>
              <div className="lp-testi-author">{t.author}</div>
              <div className="lp-testi-role">{t.role}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Final CTA ────────────────────────────────────── */}
      <section className="lp-cta" id="cta">
        <div className="lp-cta-divider" />
        <h2 className="lp-cta-title">Ready to command<br />your <em>finances</em>?</h2>
        <p className="lp-cta-sub">Join thousands of finance professionals who chose precision.</p>
        <Link to="/auth" className="lp-cta-btn">Start Free Trial</Link>
      </section>

      {/* ── Footer ───────────────────────────────────────── */}
      <footer className="lp-footer">
        <div className="lp-footer-logo">Ledger Pro</div>
        <div className="lp-footer-links">
          <a href="#features">Features</a>
          <a href="#testimonials">Clients</a>
          <a href="#cta">Contact</a>
        </div>
        <div className="lp-footer-copy">© 2026 · All rights reserved</div>
      </footer>
    </div>
  );
};

export default LandingPage;
